// src/components/layouts/Header.tsx
import React, { useEffect, useRef, useState } from "react";
import type { CurrentUser } from "../../types";
import { Menu, X } from "../icons";

interface HeaderProps {
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  title: string;
  user: CurrentUser | null;
  onLogout: () => void;
}

const Header: React.FC<HeaderProps> = ({
  sidebarOpen,
  onToggleSidebar,
  title,
  user,
  onLogout,
}) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const userInitials = user
    ? (user.username?.[0] || user.email?.[0] || "U").toUpperCase()
    : "?";

  const userDisplayName = user?.username || user?.email || "Unknown";
  const userEmail = user?.email ?? "";

  return (
    <header
      className="
        sticky top-0 z-30 h-[64px] shrink-0
        bg-white/90 backdrop-blur-xl
        border-b border-slate-200/70
        shadow-[0_1px_0_rgba(15,23,42,0.03)]
      "
    >
      <div className="h-full w-full px-5 lg:px-7 flex items-center gap-4">
        {/* Left side */}
        <div className="flex items-center gap-4">
          <button
            onClick={onToggleSidebar}
            aria-label={sidebarOpen ? "Close sidebar" : "Open sidebar"}
            className="
              inline-flex h-10 w-10 items-center justify-center
              rounded-2xl border border-slate-200 bg-white
              text-slate-600
              shadow-sm shadow-slate-200/60
              hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200
              active:scale-[0.97]
              transition-all duration-200
            "
          >
            {sidebarOpen ? (
              <X className="w-[18px] h-[18px]" />
            ) : (
              <Menu className="w-[18px] h-[18px]" />
            )}
          </button>

          {title && (
            <div className="hidden md:flex items-center gap-2">
              <span className="text-slate-300 text-sm select-none">/</span>
              <h1 className="text-[15px] font-semibold text-slate-700 tracking-[-0.2px]">
                {title}
              </h1>
            </div>
          )}
        </div>

        <div className="flex-1" />

        {/* Right side: user only */}
        {user && (
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setShowUserMenu((v) => !v)}
              className="
                flex items-center gap-3 rounded-2xl
                px-2.5 py-2
                hover:bg-slate-50
                transition-all duration-200
              "
            >
              <div className="relative shrink-0">
                <div
                  className="
                    flex h-10 w-10 items-center justify-center
                    rounded-full
                    bg-gradient-to-br from-emerald-400 to-emerald-600
                    text-white text-[14px] font-bold
                    shadow-md shadow-emerald-200/70
                    select-none
                  "
                >
                  {userInitials}
                </div>

                <span
                  className="
                    absolute bottom-0 right-0
                    h-3 w-3 rounded-full
                    bg-emerald-400 border-2 border-white
                  "
                />
              </div>

              <div className="hidden sm:flex flex-col text-left">
                <span className="text-[13.5px] font-semibold text-slate-800 leading-tight">
                  {userDisplayName}
                </span>

                {userEmail && (
                  <span className="text-[11.5px] text-slate-400 leading-tight truncate max-w-[180px]">
                    {userEmail}
                  </span>
                )}
              </div>

              <svg
                className={`w-4 h-4 text-slate-400 transition-transform duration-200 ${
                  showUserMenu ? "rotate-180" : ""
                }`}
                viewBox="0 0 16 16"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M4 6l4 4 4-4" />
              </svg>
            </button>

            {showUserMenu && (
              <div
                className="
                  absolute right-0 top-full mt-3 w-[240px]
                  overflow-hidden rounded-3xl
                  bg-white border border-slate-100
                  shadow-xl shadow-slate-200/80
                  z-50
                "
              >
                <div
                  className="
                    flex items-center gap-3
                    px-4 py-4
                    bg-gradient-to-r from-emerald-50 to-cyan-50
                    border-b border-slate-100
                  "
                >
                  <div className="relative shrink-0">
                    <div
                      className="
                        flex h-11 w-11 items-center justify-center
                        rounded-full
                        bg-gradient-to-br from-emerald-400 to-emerald-600
                        text-white text-[15px] font-bold
                        select-none
                      "
                    >
                      {userInitials}
                    </div>

                    <span
                      className="
                        absolute bottom-0 right-0
                        h-3 w-3 rounded-full
                        bg-emerald-400 border-2 border-white
                      "
                    />
                  </div>

                  <div className="min-w-0">
                    <p className="text-[13.5px] font-semibold text-slate-800 truncate leading-tight">
                      {userDisplayName}
                    </p>

                    {userEmail && (
                      <p className="text-[11.5px] text-slate-500 truncate mt-0.5">
                        {userEmail}
                      </p>
                    )}
                  </div>
                </div>

                <div className="p-2">
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      onLogout();
                    }}
                    className="
                      w-full flex items-center gap-3
                      px-3.5 py-2.5 rounded-2xl
                      text-left text-[13px] font-semibold
                      text-red-500
                      hover:bg-red-50
                      transition-colors duration-200
                    "
                  >
                    <svg
                      className="w-[15px] h-[15px] shrink-0"
                      viewBox="0 0 16 16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.6"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M6 2H3a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3" />
                      <path d="M11 11l3-3-3-3" />
                      <path d="M14 8H6" />
                    </svg>
                    Log out
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
